import { useState, useEffect, useRef } from 'react';
import { useSelector } from "react-redux";

// utils
import { formatEnum } from '../../utils/string';
import RoleScopeEnum from '../../enums/RoleScopeEnum';
import ModalWrapper from '../ModalWrapper';
import { CommonFooter, FormSchemas, FormWrapper } from '../FormComponent';
import { getAllUsersForProject, assignUserToProject } from '../../utils/api/user_apis';
import { getRolesForProject } from '../../utils/api/role_apis';
import { STATUS } from '../../utils/api/sharedfunction';
// components

const AddUsersToProjectModal = ({ isOpen, onClose, onAdd, onError }) => {
    const formRef = useRef(null);
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
  
    const selectedProject = useSelector((state) => state.projects.selectedProject);
  
    useEffect(() => {
      if (isOpen && selectedProject?.id) {
        fetchUsers();
        fetchRoles();
      }
    }, [isOpen, selectedProject?.id]);

    const handleFormSubmit = async () => {
      if (formRef.current) await formRef.current.submit();
    };
  
    const fetchUsers = async () => {
      try {
        setLoading(true);

        const response = await getAllUsersForProject({ projectId: selectedProject.id });
        
        if (response.status === STATUS.SUCCESS) {
          const availableUsers = response.data?.availableUsers || [];
          setUsers(
            availableUsers.map((user) => ({
              id: user.id,
              name: `${user.username} (${user.user_email || user.email})`,
            }))
          );
        } else {
          console.error('Failed to fetch users:', response.error);
          setUsers([]); // fallback empty
        }

      } catch (error) {
        console.error('Failed to fetch users:', error);
        setUsers([]); // fallback empty
      } finally {
        setLoading(false);
      }

    };
  
    const fetchRoles = async () => {
      try {
        setLoading(true);

        const response = await getRolesForProject(RoleScopeEnum.PROJECT_LEVEL, selectedProject.id);
        if (response.status === STATUS.SUCCESS) {
          const projectRoles = response?.data?.roles || [];
          setRoles(
            projectRoles.map((role) => ({
              id: role.id,
              name: role.name,
            }))
          );
        } else {
          console.error('Failed to fetch roles:', response.error);
          setRoles([]); // fallback empty
        }
      } catch (error) {
        console.error('Failed to fetch roles:', error);
        setRoles([]); // fallback empty
      } finally {
        setLoading(false);
      }
    };
  
    const handleConfirmAdd = async (data) => {
        try {
          setLoading(true);
      
          const response = await assignUserToProject({
            userId: data.user,
            projectId: selectedProject.id,
            roleIds: data.role,
          });
      
          if (response.status === STATUS.SUCCESS) {
            onAdd(); // assuming this refreshes list or shows toast
            onClose();
          } else {
            onError(response.error || 'Failed to add user to project', 'error');
          }
        } catch (error) {
          console.error('Error adding user to project:', error);
          onError('Failed to add user to project', 'error');
        } finally {
          setLoading(false);
        }
    };

    const fields = [
      {
        name: 'user',
        type: 'autosuggest',
        label: 'User',
        required: true,
        options: users.map((user) => ({
          value: user.id,
          label: user.name,
        })),
      },
      {
        name: 'role',
        type: 'select',
        label: 'Role',
        required: true,
        multiple: true,
        showCheckboxes: true,
        options: roles.map((role) => ({
          value: role.id,
          label: role.name,
        })),
      },
    ];


    const buttons = [
      {
        label: 'Cancel',
        variant: 'outline',
        className: 'primary',
        onClick: onClose,
        disabled: loading,
      },
      {
        label: 'Add',
        variant: 'primary',
        className: 'white bg-blue600',
        disabled: loading,
        loading: loading,
        onClick: handleFormSubmit,
      },
    ];
  
    return (
      <ModalWrapper
        isOpen={isOpen}
        onClose={onClose}
        title="Add User"
        size="small"
        footer={
          <CommonFooter
            buttons={buttons}
            align="right"
          />
        }
        children={
          <FormWrapper
            ref={formRef}
            schema={FormSchemas.addUserToProject}
            fields={fields}
            onSubmit={handleConfirmAdd}
            defaultValues={{
              user: '',
              role: [],
            }}
          />
        }
      />
    );
  };
  
  export default AddUsersToProjectModal;
